import { type GetAssetUrlShort } from "@client/lib/assets";
import { isShallowEqual } from "@client/lib/object";
import { type GenericMap, type OptionItem, type WithOptions } from "@client/types";
import { type ApiInfo } from "@client/types/Api";
import { type WithDynamicOptionsProps as WithDynamicOptionsPropsCommon } from "@common/hocs/withDynamicOptions";
import { useI18n } from "@common/hooks/i18n";
import * as React from "react";
import { getOptions } from "./lib";

export type FetchOptions<TOption = OptionItem> = (
  apiInfo?: ApiInfo,
  dependencies?: GenericMap<any>
) => Promise<TOption[]>;

export interface WithDynamicOptionsHOCProps<TOption = OptionItem> {
  apiInfo?: ApiInfo;
  options: TOption[] | string | FetchOptions<TOption>;
  getAssetUrl?: GetAssetUrlShort;
  dependencies?: GenericMap<any>;
}

export type WithDynamicOptionsCmpProps = WithOptions & {
  isLoading?: boolean;
};

const withDynamicOptionsHOC = <OriginalProps extends WithDynamicOptionsCmpProps>(
  Cmp: React.ComponentType<OriginalProps>
): React.FunctionComponent<OriginalProps & Partial<WithDynamicOptionsHOCProps>> => {
  return (props: OriginalProps & WithDynamicOptionsHOCProps) => {
    const i18n = useI18n();

    const [isLoading, setIsLoading] = React.useState(false);
    const [options, setOptions] = React.useState<OptionItem<any>[]>([]);

    const [dependencies, setDependencies] = React.useState(props.dependencies);
    React.useEffect(() => {
      if (!isShallowEqual(dependencies, props.dependencies, { deep: true })) {
        setDependencies(props.dependencies);
      }
    }, [props.dependencies]);

    React.useEffect(() => {
      if (Array.isArray(props.options)) {
        setOptions(props.options);
      } else {
        setIsLoading(true);

        (typeof props.options === "string"
          ? getOptions(i18n, props.options, props.apiInfo, props.getAssetUrl, dependencies)
          : (props.options as FetchOptions)(props.apiInfo, dependencies)
        ).then((res) => {
          setOptions(res);
          setIsLoading(false);
        });
      }
    }, [setOptions, setIsLoading, dependencies, props.apiInfo, props.getAssetUrl]); // NOTE: props.options isn't a dependency

    const { apiInfo, ...cmpProps } = props;
    return <Cmp {...(cmpProps as OriginalProps)} options={options} isLoading={isLoading} />;
  };
};
export default withDynamicOptionsHOC;

/**
 * wrap common dynamics to support Next sources, like a string or an array
 * @param Component
 * @returns
 */
export const withDynamicOptionsAdapterHOC = <OriginalProps, TOption extends OptionItem<any>>(
  Component: React.ComponentType<WithDynamicOptionsPropsCommon<OriginalProps, TOption>>
): React.FunctionComponent<OriginalProps & WithDynamicOptionsHOCProps<TOption>> => {
  return ({ dependencies, options, apiInfo, ...props }: OriginalProps & WithDynamicOptionsHOCProps<TOption>) => {
    const i18n = useI18n();

    const [deps, setDeps] = React.useState(dependencies);

    React.useEffect(() => {
      if (!isShallowEqual(deps, dependencies, { deep: true })) {
        setDeps(dependencies);
      }
    }, [dependencies]);

    const fetchOptions = React.useCallback((): Promise<TOption[]> => {
      if (Array.isArray(options)) return Promise.resolve(options);
      if (typeof options === "string") return getOptions(i18n, options, apiInfo, props.getAssetUrl, deps);

      return options(apiInfo, deps);
    }, [apiInfo, deps, options]);

    return <Component {...(props as OriginalProps)} dependent={deps} fetchOptions={fetchOptions} />;
  };
};
